/**
 * 開通（建立一個新身分）時，畫面上要說得清楚的三件事：**走到哪、花了多久、卡在哪**。
 *
 * 硬限制：後端建立身分是一支請求、**中間不回報階段**（`POST /api/sessions` 只有最終結果）。
 * 所以這裡不假裝知道「現在正在登入」，而是：
 *   1. 三段（登入 → 進房 → 掛線）固定列出來，讓人知道這一輪要做哪幾件事；
 *   2. 依該站**實測總時間**把三段切成預估區間（`estimateOpeningStage`），畫面上標「預估」；
 *   3. 超過 `OPENING_SLOW_MS` 就換一句話講清楚：已耗幾秒、這個站平常幾秒、現在最可能卡在哪一段。
 * 猜錯的代價只有一句話寫得保守（「最可能」），比「只有一顆轉圈」誠實得多。
 *
 * 數字來源：`notes/chat-ops-load.log` §1（ek21 4.2–6.1s、UT 11.3–11.9s，平均 7.7s）。
 * 這裡只拿來「說明」，不用來判斷成功／失敗——成功只認後端回應。
 */
import { serviceLabel } from './platforms';
function normalizeRoom(value) {
    const text = (value ?? '').trim();
    return text === '' ? null : text;
}
export function resolveOpeningRoom(query) {
    const targetRooms = (query.targetRooms ?? [])
        .map((room) => normalizeRoom(room))
        .filter((room) => room !== null);
    if (query.service === query.currentService) {
        return (normalizeRoom(query.currentRoom) ??
            normalizeRoom(query.pickedRoom) ??
            targetRooms[0] ??
            normalizeRoom(query.fallbackRoom));
    }
    // 跨站：目前身分／點過的房都屬於別站，只能用目標站自己的清單。
    if (targetRooms.length === 0)
        return null;
    for (const candidate of [normalizeRoom(query.currentRoom), normalizeRoom(query.pickedRoom)]) {
        if (candidate !== null && targetRooms.includes(candidate))
            return candidate;
    }
    return targetRooms[0] ?? null;
}
export const OPENING_STAGES = [
    { id: 'login', label: '登入', detail: '向站方登入這個身分' },
    { id: 'join', label: '進房', detail: '進到指定房間（有些站進不去時會自己換一間再試）' },
    { id: 'attach', label: '掛線', detail: '把收訊接上，之後才看得到別人說話' },
];
/** 各站「按下到可以說話」的實測區間（毫秒）。 */
/** 沒有實測數字的站（新接的）先用 ek21 的區間，寧可保守也不要崩。 */
const UNKNOWN_SITE_MS = { min: 4_200, max: 6_100 };
export const OPENING_EXPECTED_MS = {
    ek21: { min: 4_200, max: 6_100 },
    ut: { min: 11_300, max: 11_900 },
};
/**
 * 「比平常久」的**最短**界線（ek21 用）。
 *
 * 慢線不能是一個固定 5 秒：UT 平常就要 11–12 秒，固定 5 秒會讓它從第 5 秒起一路喊慢，
 * 使用者會學會忽略這個提示。真正的判斷用 `isOpeningSlow`（依該站自己的上限）。
 */
export const OPENING_SLOW_MS = 5_000;
/**
 * 這個站「比平常久」的界線＝該站上限再寬限 15%（UT ⇒ 約 13.7 秒，ek21 ⇒ 約 7 秒）。
 *
 * 為什麼要寬限：上限是實測最大值，剛好超過一點很常見；立刻喊慢只會製造假警報。
 */
export function openingSlowThreshold(service) {
    const { max } = OPENING_EXPECTED_MS[service] ?? UNKNOWN_SITE_MS;
    return Math.round(max * 1.15);
}
/** 現在是不是已經比這個站平常久（依該站上限判斷，不是固定秒數）。 */
export function isOpeningSlow(elapsedMs, service) {
    return elapsedMs >= openingSlowThreshold(service);
}
/** 這個站的三段切點（毫秒）；沒登記的站套保守估時。 */
export function openingStageWindow(service, id) {
    const total = (OPENING_EXPECTED_MS[service] ?? UNKNOWN_SITE_MS).max;
    const [firstBoundary = 0.4, secondBoundary = 0.85] = STAGE_BOUNDARIES;
    const index = Math.max(0, OPENING_STAGES.findIndex((stage) => stage.id === id));
    const edges = [0, total * firstBoundary, total * secondBoundary, total];
    return {
        id,
        index,
        startMs: Math.round(edges[index] ?? 0),
        endMs: Math.round(edges[index + 1] ?? total),
    };
}
/**
 * 整體進度（0–1，**推估**）：每段內依已耗時間線性內插，最後一段最多到 0.97。
 *
 * 上限 0.97 是故意的：還在跑就不會顯示「100%」，避免看起來完成了卻沒東西。
 */
export function openingProgressRatio(elapsedMs, service) {
    const total = (OPENING_EXPECTED_MS[service] ?? UNKNOWN_SITE_MS).max;
    if (elapsedMs <= 0)
        return 0;
    const ratio = Math.min(0.97, elapsedMs / total);
    return Math.round(ratio * 100) / 100;
}
/** 推估還要幾毫秒（已超過上限就回 0，交給「比平常久」的文案講）。 */
export function estimateOpeningRemainingMs(elapsedMs, service) {
    const { max } = OPENING_EXPECTED_MS[service] ?? UNKNOWN_SITE_MS;
    return Math.max(0, max - elapsedMs);
}
/**
 * 「推估還要 N 秒」那一句。
 *
 * 一定要把「推估」寫出來：後端只回最終結果，這條線是依該站平常耗時算的，
 * 不能讓它看起來像伺服器真的在回報進度。
 */
export function openingEtaLine(elapsedMs, service) {
    const site = openingSiteLabel(service);
    const range = formatExpectedRange(service);
    const remaining = estimateOpeningRemainingMs(elapsedMs, service);
    if (remaining <= 0) {
        const stage = openingStageLabel(estimateOpeningStage(elapsedMs, service));
        return `已超過 ${site} 平常的 ${range}；最可能卡在「${stage}」（推估）`;
    }
    if (remaining < 1_000)
        return `推估快完成了（${site} 平常 ${range}）`;
    return `推估還要 ${Math.ceil(remaining / 1000)} 秒（依 ${site} 平常 ${range} 推估）`;
}
/** 某一段在畫面上的補充說明：完成／推估進行中／還沒開始。 */
export function openingStepNote(id, elapsedMs, service, status) {
    const window = openingStageWindow(service, id);
    const current = estimateOpeningStage(elapsedMs, service);
    const currentIndex = OPENING_STAGES.findIndex((stage) => stage.id === current);
    if (status === 'failed' && id === current)
        return '停在這裡';
    if (window.index < currentIndex)
        return '完成（推估）';
    if (window.index === currentIndex) {
        const seconds = Math.max(1, Math.round((window.endMs - window.startMs) / 1000));
        return `進行中（推估 ${seconds} 秒內）`;
    }
    return '還沒開始';
}
/**
 * 三段的預估切點（佔「這個站上限時間」的比例）：登入約前 40%、進房到 85%、最後掛線。
 * UT 的慢主要花在進房（`GO/` 失敗換候選房），所以用比例切會自然把 UT 的進房段拉長。
 */
const STAGE_BOUNDARIES = [0.4, 0.85];
/** 現在最可能在哪一段（估算，不是後端回報）。 */
export function estimateOpeningStage(elapsedMs, service) {
    // 沒登記的站（新接的）用同一個保守估時，不要因為缺一筆就炸掉整段文案。
    const total = (OPENING_EXPECTED_MS[service] ?? UNKNOWN_SITE_MS).max;
    const ratio = elapsedMs <= 0 ? 0 : elapsedMs / total;
    const [firstBoundary = 0.4, secondBoundary = 0.85] = STAGE_BOUNDARIES;
    if (ratio < firstBoundary)
        return 'login';
    if (ratio < secondBoundary)
        return 'join';
    return 'attach';
}
export function openingStageLabel(id) {
    return OPENING_STAGES.find((stage) => stage.id === id)?.label ?? '完成';
}
/** 站別在畫面上的寫法（跟著 `platforms.ts` 的目錄；未知站回 id 本身）。 */
export function openingSiteLabel(service) {
    return serviceLabel(service);
}
/** 「N 秒」；未滿 1 秒寫「不到 1 秒」，不要出現 0 秒這種看起來像沒動的數字。 */
export function formatElapsed(elapsedMs) {
    if (elapsedMs < 1_000)
        return '不到 1 秒';
    return `${Math.round(elapsedMs / 1000)} 秒`;
}
/** 「11–12 秒」這種區間寫法（取整到秒，四捨五入後避免出現 11–11 秒）。 */
export function formatExpectedRange(service) {
    const { min, max } = OPENING_EXPECTED_MS[service] ?? UNKNOWN_SITE_MS;
    const low = Math.round(min / 1000);
    const high = Math.max(low + 1, Math.round(max / 1000));
    return `${low}–${high} 秒`;
}
/**
 * 進度區下面那一行說明。
 *
 * - 還沒比這個站平常久：講「平常多久、推估還剩幾秒」，並標明是推估。
 * - 超過：講「已耗幾秒、最可能卡在哪一段、平常多久」——這就是「卡住時要說清楚」的兌現方式。
 */
export function openingStatusLine(elapsedMs, service) {
    const site = openingSiteLabel(service);
    const range = formatExpectedRange(service);
    if (!isOpeningSlow(elapsedMs, service)) {
        // 「還要幾秒」由 `openingEtaLine` 單獨講一次，這裡不要重複。
        return `${site} 平常 ${range} 完成`;
    }
    const stage = openingStageLabel(estimateOpeningStage(elapsedMs, service));
    return `已耗 ${formatElapsed(elapsedMs)}，比平常久；${site} 平常 ${range}，現在最可能卡在「${stage}」（推估）`;
}
/**
 * 失敗要講的最後一句：**下一步是什麼**，不是重複錯誤碼。
 * 後端錯誤可能帶端點路徑或英文 enum，一律不讓它出現在畫面上（見 `humanizeOpeningError`）。
 */
export function openingFailureAdvice(service) {
    return `可以按「重試」再開一次；一直失敗就換一顆帳號或換一間房（${openingSiteLabel(service)}）。`;
}
/** 把後端錯誤翻成一句人話；認不出來就清掉技術字串後原樣縮短。 */
export function humanizeOpeningError(raw) {
    const text = (raw ?? '').trim();
    if (text === '') {
        return '沒有拿到失敗原因；再按一次「重試」，還是不行就換一顆帳號或換一間房。';
    }
    if (/401|unauthorized|登入已|登入已經過期/i.test(text)) {
        return '登入狀態失效了：請重新登入後再按「重試」。';
    }
    if (/429|太頻繁|次數過多|too many|rate.?limit|throttl/i.test(text)) {
        return '站方說這一輪登入太頻繁，先等 20–60 秒，再按重試。';
    }
    if (/403|地區|台灣|region|forbidden/i.test(text)) {
        return '站方不接受這次登入（多半是來源地區被擋），換一顆帳號或稍後再試。';
    }
    if (/暱稱|nickname|撞名|正在使用/i.test(text)) {
        return '暱稱撞名了：再按一次重試，系統會自己換一個名字。';
    }
    if (/房間|room|進不去|join/i.test(text)) {
        return '房間進不去：先換一間房，再按重試。';
    }
    if (/連不到|Failed to fetch|network|timeout|timed out|ETIMEDOUT|abort/i.test(text)) {
        return '連不到後端（網路或後端暫時沒回應），確認連線後再按重試。';
    }
    if (/50[0234]|500|內部錯誤|server error|unavailable/i.test(text)) {
        // 只講原因；「按重試」由 openingFailureAdvice 說一次就好，不要同一句講兩次。
        return '後端或站方暫時出錯。';
    }
    return shorten(scrubTechnical(text));
}
/**
 * 清掉不該給使用者看的技術片段：HTTP 動詞＋路徑、`/api/...`、JSON 片段、英文狀態列舉名稱。
 * 保留中文說明（那通常已經是後端翻好的話）。
 */
export function scrubTechnical(text) {
    return text
        .replace(/\b(GET|POST|PUT|PATCH|DELETE)\s+\/[^\s，。；、)）]*/gi, '這個動作')
        .replace(/\/api\/[^\s，。；、)）]*/g, '後端')
        .replace(/\{[^{}]*\}/g, '')
        .replace(/\b(HTTP|JSON|fetch)\b/gi, '')
        .replace(/[A-Za-z_]+_[a-z_]+/g, '')
        .replace(/[（(]\s*[)）]/g, '')
        .replace(/\s{2,}/g, ' ')
        .replace(/^[：:，,。\s]+/, '')
        .trim();
}
function shorten(text) {
    if (text === '')
        return '開通沒有完成，再按一次「重試」。';
    return text.length > 64 ? `${text.slice(0, 64)}…` : text;
}
//# sourceMappingURL=opening.js.map